<template>
  <div class="ma-4">
    <v-row>
      <!-- Calendar -->
      <v-col class="col-lg-5">
        <v-card outlined class="pt-2">
          <v-date-picker
            ref="datePicker"
            v-model="dates"
            class="elevation-0 pb-2"
            full-width
            :picker-date.sync="pickerDate"
            :events="eventDates"
            event-color="green"
            color="primary"
            :first-day-of-week="1"
            range
            :locale="$store.getters.getLanguageLocale().slice(0, 2)"
            landscape
            no-title
            @input="reloadData()"
          />
        </v-card>
      </v-col>
      <v-col>
        <date-picker-today-button :date-picker-object="$refs.datePicker">
        </date-picker-today-button>
      </v-col>

      <!-- Add new extra ride btn -->
      <v-col align="end">
        <v-btn
          class="primary"
          large
          @click="createNewExtraRide(rideInstance.startDate)"
        >
          <i class="fas fa-plus mr-2" />
          {{ $t('extraRides.newExtraRide') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-card outlined>
          <div class="d-flex justify-space-between">
            <v-card-title>
              {{ $t('extraRides.extraRides') }} {{ datesFormatted }}
            </v-card-title>
          </div>
          <v-card-text>
            <!-- Table for infos about extra rides on selected date / date range -->
            <v-data-table
              :headers="rideInfoHeadings"
              :items="rideInstance.rides"
              :no-data-text="$t('extraRides.noDataAvailable')"
              :footer-props="{
                itemsPerPageOptions: [-1, 5, 20],
                itemsPerPageText: $t('general.rowsPerPage'),
                itemsPerPageAllText: $t('dataTable.allRows'),
              }"
            >
              <template #body="{ items }">
                <tbody>
                  <tr v-for="(ride, rideIndex) in items" :key="rideIndex">
                    <td @click="editExtraRide(ride.id)">
                      <span
                        v-if="ride.ascentDate"
                        class="far fa-calendar-day mr-1"
                      ></span
                      >{{ ride.ascentDate | formatDate }}
                      <span
                        v-if="ride.ascentDate"
                        class="far fa-clock fa-md ml-2 mr-1"
                      ></span
                      >{{ getTime(ride.ascentDate) }}
                    </td>
                    <td @click="editExtraRide(ride.id)">
                      <span
                        v-if="ride.descentDate"
                        class="far fa-calendar-day mr-1"
                      ></span
                      >{{ ride.descentDate | formatDate }}
                      <span
                        v-if="ride.descentDate"
                        class="far fa-clock fa-md ml-2 mr-1"
                      ></span
                      >{{ getTime(ride.descentDate) }}
                    </td>
                    <td @click="editExtraRide(ride.id)">
                      {{ ride.user.firstname }} {{ ride.user.surname }}
                    </td>
                    <td @click="editExtraRide(ride.id)">
                      <span class="far fa-calendar-day mr-1"></span>
                      {{ ride.updatedAt | formatDate }}
                      <span class="far fa-clock fa-md ml-2 mr-1"></span>
                      {{ getTime(ride.updatedAt) }}
                    </td>
                    <td @click="editExtraRide(ride.id)">{{ ride.comment }}</td>
                    <td class="text-end">
                      <!-- edit button -->
                      <v-btn icon class="mr-2" @click="editExtraRide(ride.id)">
                        <i class="fal fa-pen"></i>
                      </v-btn>

                      <!-- delete button -->
                      <v-btn
                        class="red"
                        icon
                        @click="$refs.confirmDeletionModal[rideIndex].show()"
                      >
                        <span>
                          <i class="fal fa-trash"></i>
                        </span>
                        <s-modal
                          ref="confirmDeletionModal"
                          :size="1"
                          :headerText="$t('extraRides.confirmDeletion')"
                          classHeader="text-center"
                          :okText="$t('extraRides.deleteExtraRide')"
                          @ok="deleteExtraRide(ride.id)"
                        >
                        </s-modal>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ExtraRides from '@/classes-internal/extraRides/ExtraRides.js'
import DateHelper from '@/classes-shared/utils/DateHelper'
import SModal from '../../components-shared/utils/SModal.vue'
import moment from 'moment'
import DatePickerTodayButton from '../booking/DatePickerTodayButton'
import { shopInstance } from '@/classes-shared/utils/axiosInstance'

export default {
  name: 'ExtraRide',
  components: { DatePickerTodayButton, SModal },

  data() {
    return {
      dates: [],
      rideInstance: new ExtraRides(),
      pickerDate: null,
      eventDates: [],
    }
  },

  computed: {
    rideInfoHeadings() {
      return [
        {
          text: this.$t('extraRides.ascent'),
          sortable: true,
          value: 'ascentDate',
        },
        {
          text: this.$t('extraRides.descent'),
          sortable: true,
          value: 'descentDate',
        },
        {
          text: this.$t('extraRides.addedBy'),
          sortable: true,
          value: 'addedBy',
        },
        {
          text: this.$t('extraRides.updatedAt'),
          sortable: true,
          value: 'updatedAt',
        },
        {
          text: this.$t('extraRides.comment'),
          sortable: true,
          width: '320px',
          value: 'comment',
        },
        {
          text: this.$t('extraRides.edit'),
          align: 'right',
          sortable: false,
        },
      ]
    },
    datesFormatted() {
      if (!this.dates.length) {
        return (
          i18n.t('extraRides.fromDate') +
          ' ' +
          this.$options.filters.formatDate(new Date())
        )
      }
      if (this.dates.length === 2) {
        let from = this.$options.filters.formatDate(this.dates[0])
        let to = this.$options.filters.formatDate(this.dates[1])

        if (this.dates[0] > this.dates[1]) {
          const tmpTo = to
          to = from
          from = tmpTo
        }

        return (
          i18n.t('extraRides.fromDate') +
          ' ' +
          from +
          ' ' +
          i18n.t('extraRides.toDate') +
          ' ' +
          to
        )
      } else {
        return (
          i18n.t('extraRides.fromDate') +
          ' ' +
          this.$options.filters.formatDate(this.dates[0])
        )
      }
    },
  },

  watch: {
    dates() {
      return this.rideInstance.ride
    },
    pickerDate(val) {
      if (val) {
        this.getExtraRidesForSelectedMonth(val)
      }
    },
  },

  mounted() {
    const ascentDate = this.$route.query?.ascentDate
    const descentDate = this.$route.query?.descentDate
    if (ascentDate) {
      this.dates.push(ascentDate)
      this.pickerDate = ascentDate
    }

    if (descentDate) this.dates.push(descentDate)
    if (descentDate && !ascentDate) {
      this.pickerDate = descentDate
    }

    this.reloadData()
  },

  methods: {
    getTime(dateString) {
      if (!dateString) return null
      return moment(dateString).format('HH:mm')
    },
    reloadData(dates = this.dates) {
      this.rideInstance.loadExtraRides(dates)
      this.getExtraRidesForSelectedMonth()
    },
    createNewExtraRide(date) {
      // Pass currently selected date to booking page (default is todays date)
      if (date) {
        const targetDate = moment(date)
        if (!targetDate.isSameOrAfter(moment(), 'day')) {
          date = new Date().toISOString().substr(0, 10)
        }
        return this.$router.push({
          name: 'createExtraRide',
          params: { ascentDate: date },
        })
      }
    },
    editExtraRide(rideId) {
      this.$router.push({ name: 'editExtraRide', params: { id: rideId } })
    },
    getExtraRidesForSelectedMonth(val = this.pickerDate) {
      // get currently selected month
      let year = parseInt(val.slice(0, 4))
      let month = parseInt(val.slice(5, 7))
      let beginOfMonth = DateHelper.shiftLocalToUtcIsoString(
        new Date(year, month - 1)
      )
      let endOfMonth = DateHelper.shiftLocalToUtcIsoString(
        new Date(year, month + 1, 0, 23, 59, 59, 999)
      )
      this.getRideDate(beginOfMonth, endOfMonth)
    },

    async getRideDate(beginOfMonth, endOfMonth) {
      try {
        const response = await shopInstance().get(
          'admin/extraRide/' + beginOfMonth + '/' + endOfMonth
        )
        this.eventDates = []

        for (let i = 0; i < response.data.length; i++) {
          // Check if extra ride has ascent date, if not use descent date for display of events in calendar
          let date = response.data[i].ascentDate
            ? response.data[i].ascentDate
            : response.data[i].descentDate
          this.eventDates.push(
            DateHelper.shiftLocalToSimpleDateString(
              DateHelper.shiftUtcToLocal(new Date(date))
            )
          )
        }
      } catch (e) {
        EventBus.$emit('notify', i18n.t('extraRides.updateNotSuccessfull'))
      } finally {
        return Promise
      }
    },

    async deleteExtraRide(id) {
      await this.rideInstance.deleteExtraRide(id)
      this.reloadData(this.dates)
    },
  },
}
</script>

<style scoped>
tr {
  cursor: pointer;
}
.fa-trash {
  color: white;
}
s-modal {
  text-align: center;
}
</style>
